// shared/types.ts — Break Glass domain types + interfaces.
// Single source of truth for both /api and /web. No runtime logic here.

// ---------------------------------------------------------------------------
// Enums (as const unions — Workers + Vite friendly, no `enum` keyword)
// ---------------------------------------------------------------------------

/**
 * Kinds of audit-log events (hash-chained, append-only). The zero-trust quorum
 * lifecycle kinds (ADR-002 D4) are additive; payloads NEVER carry material —
 * only ids, counts, pubkey ids, share indices, timestamps, and outcomes.
 */
export type AuditKind =
  | 'requested'
  | 'approved'
  | 'denied'
  | 'revealed'
  | 'revoked'
  | 'expired'
  | 'created'
  // --- zero-trust quorum lifecycle (ADR-002) ---
  | 'key.published'
  | 'key.revoked'
  | 'record.sealed'
  | 'record.redealt'
  | 'unseal.requested'
  | 'shard.submitted'
  | 'record.unlocked'
  | 'possession.proven'
  | 'possession.overdue'
  | 'ownership.transferred'
  // --- enrollment lobby/admit lifecycle (ADR-003 D3) ---
  | 'member.admitted'
  | 'member.rejected';

export const AUDIT_KINDS: readonly AuditKind[] = [
  'requested',
  'approved',
  'denied',
  'revealed',
  'revoked',
  'expired',
  'created',
  'key.published',
  'key.revoked',
  'record.sealed',
  'record.redealt',
  'unseal.requested',
  'shard.submitted',
  'record.unlocked',
  'possession.proven',
  'possession.overdue',
  'ownership.transferred',
  'member.admitted',
  'member.rejected',
] as const;

/** Sensitivity classification of a secret. Drives UI badge tone + policy. */
export type Sensitivity = 'low' | 'medium' | 'high' | 'critical';

export const SENSITIVITY_LEVELS: readonly Sensitivity[] = [
  'low',
  'medium',
  'high',
  'critical',
] as const;

/** Principal roles. Admin can revoke any grant + manage the vault. */
export type Role = 'admin' | 'approver' | 'operator';

/** Notification event kinds (stubbed transport). */
export type NotificationKind =
  | 'request_raised'
  | 'request_approved'
  | 'request_denied'
  | 'grant_expiring'
  | 'grant_revoked';

// ---------------------------------------------------------------------------
// Domain entities (mirror the D1 tables in /api/schema.sql)
// ---------------------------------------------------------------------------

/** An authenticated actor. Returned by AuthProvider; never trusts the client. */
export interface Principal {
  id: string;
  name: string;
  email: string;
  role: Role;
}

// LEGACY (ADR-003 D2 / C1): the custodial `Secret(ciphertext, iv)` model and the
// single-approver `AccessRequest` / `Grant` types are RETIRED. Their tables
// (`secrets`, `requests`, `grants`) are dropped in migration 0003 and every
// runtime path that touched them is deleted. The live zero-trust model is
// `SecretRecord` + `SecretMember` + the unseal-ceremony types below.

/** An append-only, hash-chained audit entry. */
export interface AuditEntry {
  id: string;
  seq: number; // monotonic chain position
  kind: AuditKind;
  actorId: string;
  actorName: string;
  payload: Record<string, unknown>; // stored as payload_json
  prevHash: string; // sha256 hex of the previous entry's `hash`
  hash: string; // sha256 hex of (prevHash + canonical payload)
  createdAt: number; // epoch ms
}

/** A stubbed notification record (Notifier writes these to D1). */
export interface NotificationRecord {
  id: string;
  kind: NotificationKind;
  payload: Record<string, unknown>;
  createdAt: number; // epoch ms
}

// ---------------------------------------------------------------------------
// Zero-trust quorum metadata model (ADR-002). The server is a metadata + identity
// ledger and an opaque-ciphertext relay. NONE of these types carry plaintext, a
// symmetric key, or a private key — the only secret-adjacent field is an OPAQUE
// shard ciphertext encrypted to a recipient's PUBLIC key (R11).
// ---------------------------------------------------------------------------

/** Lifecycle status of a sealed secret record. */
export type SecretRecordStatus = 'sealed' | 'redealing' | 'revoked';

/** Public-key algorithms accepted by the registry (mirror ADR-001). */
export type KeyAlg = 'ECDH-ES' | 'RSA-OAEP-256';

/** How the holder's private key is custodied (the wrap is client-side). */
export type KeyKind = 'software' | 'webauthn-prf-wrapped';

/** Status of a registered public key. */
export type KeyStatus = 'active' | 'revoked';

/** Lifecycle status of a break-glass unseal ceremony. */
export type UnsealStatus =
  | 'gathering'
  | 'unlocked'
  | 'expired'
  | 'revoked'
  | 'denied';

/** Status of a possession-proof challenge. */
export type PossessionStatus = 'pending' | 'proven' | 'overdue' | 'failed';

/**
 * Metadata envelope for a sealed secret (mirrors `secret_record`). NO material:
 * `envelopeMeta` holds only PUBLIC envelope params (alg id, scheme id, version,
 * salt/nonce for the envelope-key split) — never a key or plaintext (ADR-001).
 */
export interface SecretRecord {
  id: string;
  description: string;
  sensitivity: Sensitivity;
  ownerId: string; // accountability only (R15)
  thresholdK: number; // shares required to reconstruct
  totalN: number; // eligible set size
  envelopeMeta: string; // PUBLIC params JSON; never key/plaintext
  /**
   * OPAQUE AES-GCM ciphertext of the secret (base64url), encrypted under the
   * Shamir-split data key. The server holds no data key and can NEVER decrypt it,
   * so persisting/serving it preserves zero-trust (R11). null only in legacy rows
   * sealed before this field existed. Returned on the read-path to an authorized
   * owner/holder so the requester's combineAndOpen can run after gathering K shares.
   */
  envelopeCiphertext: string | null;
  timeoutSeconds: number;
  /**
   * Owner-defined possession-proof cadence in SECONDS (M2.7). The minimum interval
   * between required liveness proofs for this record's holders; the pending-proof
   * inbox lazily mints a due challenge once (now - last proof) >= this. Default 7
   * days. PUBLIC metadata — it is a policy knob, never key material.
   */
  proofIntervalSec: number;
  status: SecretRecordStatus;
  createdAt: number; // epoch ms
  createdBy: string; // principal id
}

/**
 * A registered PUBLIC key bound to a principal (mirrors `key_registry`). Public
 * material only: the JWK is a public key, `attestation` is a server-issued binding
 * token, `safetyNumber` a derived fingerprint. No private key ever lands here.
 */
export interface RegisteredKey {
  pubkeyId: string; // JWK thumbprint (RFC 7638)
  principalId: string; // OIDC sub (or stub id) bound to this key
  email: string;
  publicKeyJwk: string; // the PUBLIC JWK
  alg: KeyAlg;
  keyKind: KeyKind;
  attestation: string; // server-issued pubkey<->principal<->email binding
  safetyNumber: string; // out-of-band fingerprint
  status: KeyStatus;
  createdAt: number; // epoch ms
}

/** Client-safe projection of a RegisteredKey (identical — it is all public). */
export type RegisteredKeyPublic = RegisteredKey;

/**
 * A member of a secret's eligible set N (mirrors `secret_member`). One member = one
 * shard slot. `shardCiphertext` is OPAQUE ciphertext encrypted to this member's
 * PUBLIC key (ADR-001 WireShard), or null in WebRTC-delivered mode. The server
 * cannot decrypt it — it holds no private key.
 */
export interface SecretMember {
  id: string;
  secretId: string;
  email: string;
  pubkeyId: string;
  shareIndex: number; // Shamir x-coordinate (PUBLIC; not the share)
  shardCiphertext: string | null; // OPAQUE ciphertext or null (transient)
  shardRevoked: boolean;
  addedAt: number; // epoch ms
  /**
   * When this member last successfully answered a possession proof (epoch ms), or
   * null if never. Stamped on a verified answer; drives the due calc in the
   * pending-proof inbox against the record's `proofIntervalSec`. PUBLIC timestamp.
   */
  lastProvenAt: number | null;
}

/**
 * A break-glass unseal ceremony (mirrors `unseal_request`). Tracks WHICH shards
 * arrived (the bolt count), never the shards themselves.
 */
export interface UnsealRequest {
  id: string;
  secretId: string;
  requesterId: string;
  requesterPubkeyId: string; // key shards are re-encrypted TO for delivery
  /**
   * The requester's EPHEMERAL ECDH P-256 PUBLIC key (JWK JSON), generated in the
   * requester's browser when the ceremony opens and published here so holders can
   * re-encrypt their decrypted share TO it before it crosses any wire (M3.2). The
   * server stores it as opaque PUBLIC metadata — the matching private key never
   * leaves the requester's device, so the server can never read a delivered share.
   * null for legacy ceremonies opened before this field existed.
   */
  requesterEphemeralJwk: string | null;
  justification: string;
  status: UnsealStatus;
  submittedCount: number;
  thresholdK: number; // denormalized from the target record (UX convenience)
  /**
   * Human join code (what3words style) for the ceremony — the opener mints it so a
   * holder can resolve THIS open ceremony by code + PIN (mirror enrollment, ADR-003
   * D3). PUBLIC; the matching PIN is never stored in the clear. null for legacy
   * ceremonies opened before this field existed.
   */
  joinWords: string | null;
  createdAt: number; // epoch ms
  expiresAt: number; // epoch ms
}

/**
 * One outstanding possession-proof challenge in the CALLER's pending-proof inbox
 * (GET /proofs/pending). The caller signs `nonce` with the registered key to clear
 * the due state and stamp `lastProvenAt`. PUBLIC metadata + a public nonce only —
 * a signature over it proves key control, never reveals a shard or secret.
 */
export interface PendingProof {
  proofId: string;
  secretId: string;
  recordName: string; // the record's description (display)
  nonce: string; // server-issued PUBLIC challenge to sign
  issuedAt: number; // epoch ms
}

/** Context a holder needs to connect to an unseal ceremony resolved by join code
 * (POST /quorum/join). Metadata + the requester's PUBLIC ephemeral key only — the
 * holder then fetches their own shard via the existing my-shard path (R11). */
export interface UnsealJoinContext {
  requestId: string;
  secretId: string;
  recordName: string;
  requesterEphemeralJwk: string | null;
}

/**
 * The metadata of a single shard arrival (mirrors `shard_submission` + ceremony
 * progress). Carried by the `shard.submitted` realtime event. Carries NO
 * ciphertext and NO share — the opaque blob travels peer-to-peer (WebRTC) or via
 * a requester-targeted DO relay queue, never on this event (ADR-002 D2 / A4).
 */
export interface ShardSubmission {
  requestId: string;
  memberId: string;
  shareIndex: number;
  submittedCount: number;
  thresholdK: number;
  at: number; // epoch ms
}

/**
 * A possession-proof challenge (mirrors `possession_proof`). A signature over a
 * PUBLIC server nonce, verifiable with the PUBLIC key — proves key control only.
 */
export interface PossessionProof {
  id: string;
  secretId: string;
  memberId: string;
  pubkeyId: string;
  nonce: string; // server-issued public challenge
  issuedAt: number; // epoch ms
  expiresAt: number; // epoch ms
  signature: string | null; // holder's signature over nonce; null until answered
  verifiedAt: number | null; // when the server verified
  status: PossessionStatus;
}

// ---------------------------------------------------------------------------
// Pluggable interfaces (stubbed now, real impls drop in later)
// ---------------------------------------------------------------------------

/**
 * Resolves the authenticated principal from an inbound request. Stubbed by
 * StubAuthProvider; ZitadelAuthProvider drops in without touching call sites.
 */
export interface AuthProvider {
  getPrincipal(req: Request): Promise<Principal>;
}

/**
 * Emits a notification for a lifecycle event. Stubbed by StubNotifier (writes
 * to the `notifications` table); a real email transport drops in later.
 */
export interface Notifier {
  notify(kind: NotificationKind, payload: Record<string, unknown>): Promise<void>;
}

// ---------------------------------------------------------------------------
// Realtime wire protocol (DO <-> clients). Server is the only writer.
// ---------------------------------------------------------------------------

/**
 * Server-pushed realtime event envelope broadcast by RealtimeDO. The DO is the
 * single writer (R4). The zero-trust quorum variants (ADR-002 D2) are additive;
 * `shard.submitted` carries CIPHERTEXT-FREE metadata only (the opaque shard
 * travels peer-to-peer or via a requester-targeted relay queue, never here).
 */
export type RealtimeEvent =
  | { type: 'audit.appended'; entry: AuditEntry }
  | { type: 'hello'; now: number }
  // --- zero-trust quorum relay (ADR-002 D2) ---
  | { type: 'unseal.created'; request: UnsealRequest }
  | { type: 'shard.submitted'; submission: ShardSubmission }
  | { type: 'request.unlocked'; request: UnsealRequest }
  // A holder posted an OPAQUE encrypted share to the store-and-forward relay
  // (M3.2). METADATA ONLY — the blob is NOT in this event; the requester drains
  // it via GET /quorum/:requestId/deliveries. Signals "go pull" to the requester.
  | { type: 'shard.delivered'; delivery: ShardDeliveredEvent }
  // WebRTC signaling relayed peer→peer over the DO for the SECONDARY P2P path
  // (M3.2). Audience-scoped to the ceremony's participants. `payload` is opaque
  // SDP/ICE — it carries no share, no key, and no plaintext; it only lets two
  // browsers negotiate a direct DataChannel. Best-effort; failure falls back to
  // the store-and-forward relay above.
  | { type: 'webrtc.signal'; signal: WebRtcSignal };

/** Metadata-only announcement that an opaque share blob is waiting to be drained. */
export interface ShardDeliveredEvent {
  requestId: string;
  memberId: string;
  shareIndex: number;
}

/**
 * An opaque WebRTC signaling frame relayed over the DO (M3.2 SECONDARY path).
 * `kind` discriminates SDP offer/answer vs an ICE candidate; `from`/`to` are
 * participant principal ids so peers pair up. `payload` is the opaque
 * SDP/candidate JSON — it negotiates a direct channel and carries NO share/key.
 */
export interface WebRtcSignal {
  requestId: string;
  kind: 'offer' | 'answer' | 'ice';
  from: string; // sender principal id
  to: string; // intended recipient principal id (peers filter on this)
  payload: string; // opaque SDP / ICE candidate JSON
}

// ---------------------------------------------------------------------------
// Zero-trust quorum HTTP payloads (ADR-002 D3). Validated server-side (R10.3).
// ---------------------------------------------------------------------------

/**
 * Publish a PUBLIC key to the registry. The Worker derives `pubkeyId` (JWK
 * thumbprint), binds it to the caller's principal + email, computes a safety
 * number and issues an attestation. A body containing ANY private-JWK field
 * (`d`, `p`, `q`, `dp`, `dq`, `qi`) is rejected — defense in depth.
 */
export interface PublishKeyInput {
  publicKeyJwk: string; // the PUBLIC JWK only
  alg: KeyAlg;
  keyKind: KeyKind;
}

/**
 * Submit a shard arrival for an unseal ceremony. CIPHERTEXT-FREE: the server
 * records only THAT member `memberId` submitted (the opaque encrypted shard
 * travels peer-to-peer or via the requester-targeted relay queue, never here).
 */
export interface SubmitShardInput {
  memberId: string;
}

/**
 * Deliver an encrypted share over the store-and-forward relay (M3.2). `blob` is
 * the holder's DECRYPTED Shamir share RE-ENCRYPTED to the requester's ephemeral
 * PUBLIC key (a WireShard JSON, ADR-001) — OPAQUE to the server, which only
 * stores/relays it keyed by (requestId, memberId). The server holds no private
 * key and can never read it. `shareIndex` is the PUBLIC Shamir x-coordinate.
 */
export interface DeliverShardInput {
  memberId: string;
  shareIndex: number;
  blob: string; // OPAQUE ciphertext (WireShard JSON), encrypted to the requester's ephemeral pubkey
}

/**
 * One opaque delivered share, drained by the ceremony's requester via GET
 * /quorum/:requestId/deliveries. The server returns only the requester's OWN
 * ceremony's blobs (403 to anyone else). `blob` stays opaque end-to-end.
 */
export interface ShardDelivery {
  memberId: string;
  shareIndex: number;
  blob: string; // OPAQUE ciphertext; decryptable only with the requester's ephemeral private key
  deliveredAt: number;
}

// ---------------------------------------------------------------------------
// Sealing write-path (ADR-003 D1). POST /records — the single atomic write that
// activates the zero-trust schema. The server receives CIPHERTEXT + METADATA
// ONLY: never the secret, the data key, or any private key.
// ---------------------------------------------------------------------------

/** One shard slot in a seal request. `shardCiphertext` is an OPAQUE WireShard. */
export interface SealMemberInput {
  pubkeyId: string; // MUST be admitted in the session + active in key_registry
  email: string; // must match the registry binding for this pubkeyId
  shareIndex: number; // public Shamir x-coord; UNIQUE within the record (1..N)
  shardCiphertext: string | null; // opaque ciphertext; null only in transient mode
}

/**
 * Body of POST /records (ADR-003 D1). Validated + recomputed server-side; the
 * server trusts none of the counts. `envelopeMeta` holds PUBLIC envelope params
 * only — any private-JWK field is rejected (no-material assertion, gate #4).
 */
export interface SealRecordInput {
  sessionId: string; // the enrollment session this seals (admit set is authoritative)
  record: {
    id: string; // client-generated id; server re-checks uniqueness
    description: string;
    sensitivity: Sensitivity;
    ownerId: string; // responsible party; defaults to initiator (R15)
    thresholdK: number; // 2 <= K <= N
    totalN: number; // = members.length; server recomputes
    timeoutSeconds: number;
    /** Owner-defined possession-proof cadence (seconds). Optional; defaults to 7
     * days server-side. Validated > 0. PUBLIC policy metadata (M2.7). */
    proofIntervalSec?: number;
    envelopeMeta: Record<string, unknown>; // PUBLIC params only; NO key, NO plaintext
    /**
     * OPAQUE AES-GCM ciphertext of the secret (base64url), encrypted under the
     * Shamir-split data key. The server cannot decrypt it (no data key); it is
     * stored verbatim and relayed back on the read-path so the requester can open
     * the record at quorum. The no-material scan rejects JWK private fields here.
     */
    envelopeCiphertext: string;
  };
  members: SealMemberInput[]; // exactly N entries
}

/** Receipt returned by POST /records on success (ADR-003 D1 gate #6). */
export interface SealReceipt {
  id: string;
  status: 'sealed';
  n: number;
  k: number;
  sealedAt: number;
}

// ---------------------------------------------------------------------------
// Records read-path + responsibility/reporting projections (M4.2/M4.3/M4.7/M4.8;
// R15). Metadata only — these shapes NEVER carry plaintext, a key, or a shard
// ciphertext. A holder fetches their own opaque shard via a separate path, not
// here. Counts, statuses, public keys/fingerprints, and timestamps only.
// ---------------------------------------------------------------------------

/**
 * A row in GET /records — a record the caller owns or holds a shard in. Carries
 * quorum-health context (how many distinct holders have a live possession proof)
 * so the list can render a security-state badge without any material.
 */
export interface RecordSummary {
  id: string;
  description: string;
  sensitivity: Sensitivity;
  ownerId: string;
  thresholdK: number;
  totalN: number;
  status: SecretRecordStatus;
  timeoutSeconds: number;
  createdAt: number;
  /** Live members (non-revoked shard slots). */
  memberCount: number;
  /** Members whose latest possession proof is `proven` (quorum health signal). */
  provenCount: number;
  /** True iff the caller is the record's responsible party (R15). */
  isOwner: boolean;
  /** True iff the caller holds a (non-revoked) shard slot in this record. */
  isHolder: boolean;
}

/** One member in a record-detail view — identity + key fingerprint + custody
 * status. PUBLIC material only: email, pubkey id, safety-number fingerprint, the
 * latest possession-proof status, and whether the shard slot is revoked. */
export interface RecordMemberView {
  memberId: string;
  email: string;
  pubkeyId: string;
  /** Out-of-band safety number from the registry (null if the key is gone). */
  fingerprint: string | null;
  shareIndex: number;
  shardRevoked: boolean;
  /** Whether the server holds an opaque store-and-forward shard for this slot. */
  hasStoredShard: boolean;
  /** Latest possession-proof status for this member, or null if never issued. */
  possessionStatus: PossessionStatus | null;
  lastProofAt: number | null;
}

/** GET /records/:id — full detail (metadata only). Members + threshold + timeout
 * + ceremony history (from the audit) + owner. No shard ciphertext is returned. */
export interface RecordDetail {
  record: SecretRecord;
  members: RecordMemberView[];
  /** Unseal ceremonies that occurred against this record (newest first). */
  ceremonies: UnsealRequest[];
  /** Audit slice for this record (ascending; ids/counts only — no material). */
  history: AuditEntry[];
}

/** One record in GET /responsibilities — a record the caller owns, with the
 * reporting view: roster + last possession proof per member + ceremonies (R15). */
export interface ResponsibilityView {
  record: SecretRecord;
  members: RecordMemberView[];
  ceremonies: UnsealRequest[];
}

// ---------------------------------------------------------------------------
// Enrollment / lobby / admit + magic-link (ADR-003 D3 / D7). The join code grants
// LOBBY ENTRY ONLY; trust is conferred by the human admit, never by code/token.
// ---------------------------------------------------------------------------

/** Lifecycle status of an enrollment session. */
export type EnrollmentStatus = 'open' | 'sealed' | 'closed' | 'expired';

/** Lobby/admit state of a joiner within a session (ADR-003 D3). */
export type MemberState = 'joined' | 'admitted' | 'rejected' | 'sealed';

/** How a joiner authenticated into the lobby (shown at the admit gate). */
export type AuthMethod = 'oidc' | 'magic-link';

/** A joiner's lobby/admit record within a session (mirrors `session_member`). */
export interface SessionMember {
  id: string;
  sessionId: string;
  principalId: string;
  pubkeyId: string;
  email: string;
  authMethod: AuthMethod;
  state: MemberState;
  joinedAt: number;
  admittedAt: number | null;
}

/** Admit/reject a joiner in a session (initiator-only). */
export interface AdmitInput {
  pubkeyId: string;
}

/** Mint a single-use, TTL'd, session-bound magic link for a guest (D7). */
export interface InviteGuestInput {
  email: string;
}

/** Consume a magic-link token (D7). Bound to the session it was minted for. */
export interface VerifyMagicLinkInput {
  token: string;
}
